import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { DatePeriodFilter } from '#components/shared/DatePeriodFilter.tsx';
import { Filter, getOption } from '#components/shared/Filter.tsx';
import { FilterResetButton } from '#components/shared/FilterResetButton.tsx';
import { FleetGroupFilter } from '#components/shared/FleetGroupFilter.tsx';
import { SearchInput } from '#components/shared/SearchInput.tsx';
import type { FleetGroup } from '#domain/FleetGroup.ts';
import type { DatePeriodFilterState } from '#hooks/shared/useDatePeriodFilter.ts';

import { TourStatusData } from '../model/TourStatusData.ts';
import type { TourTableColumnConfig } from './TourTable.tsx';

const getOptions = (intl: IntlShape) => [
    getOption('problem', TourStatusData, intl),
    getOption('in-transit', TourStatusData, intl),
    getOption('planned', TourStatusData, intl),
    getOption('finished', TourStatusData, intl),
];

type TourTableToolbarProps = {
    search: string;
    onSearch: (value: string) => void;
    statusFilter: string[];
    onFilterStatus: (input: string[]) => void;
    groupFilter: string[];
    onFilterGroups: (input: string[]) => void;
    fleetGroups: FleetGroup[];
    isFleetGroupsLoading: boolean;
    isFleetGroupsDisabled: boolean;
    onResetFilters: () => void;
    dateFilter: DatePeriodFilterState;
    onOpenCreateTourDialog: () => void;
    columns: TourTableColumnConfig[];
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    onResetColumns: () => void;
    onViewTypeChange: (viewType: TableViewTogglesViewType) => void;
    viewType: TableViewTogglesViewType;
};

export const TourTableToolbar = ({
    search,
    onSearch,
    statusFilter,
    onFilterStatus,
    groupFilter,
    onFilterGroups,
    fleetGroups,
    isFleetGroupsLoading,
    isFleetGroupsDisabled,
    onResetFilters,
    dateFilter,
    onOpenCreateTourDialog,
    columns,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    onViewTypeChange,
    viewType,
}: TourTableToolbarProps) => {
    const intl = useIntl();
    const options = useMemo(() => getOptions(intl), [intl.locale]);
    const hasActiveFilter = statusFilter.length > 0 || groupFilter.length > 0 || dateFilter.hasActiveFilter;

    return (
        <TableToolbar>
            <TableToolbarColumn>
                <ButtonToolbar>
                    <Button
                        bsStyle={Button.PRIMARY}
                        iconName='plus'
                        autoTrackingKey='intl-msg:ordercom-cloud-frontend.tours.create-dialog.title'
                        onClick={onOpenCreateTourDialog}
                    >
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.title',
                        })}
                    </Button>
                </ButtonToolbar>
            </TableToolbarColumn>

            <TableToolbarColumn horizontalAlign='right' separator={hasActiveFilter}>
                <FilterResetButton
                    hasActiveFilter={hasActiveFilter}
                    onResetFilter={onResetFilters}
                    trackingKey='ordercom.tours.table-toolbar.reset-filter'
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <DatePeriodFilter {...dateFilter} compact />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <Filter onFilter={onFilterStatus} options={options} values={statusFilter} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <FleetGroupFilter
                    onChange={onFilterGroups}
                    fleetGroups={fleetGroups}
                    value={groupFilter}
                    isLoading={isFleetGroupsLoading}
                    disabled={isFleetGroupsDisabled}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <SearchInput onSearch={onSearch} value={search} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableViewToggles
                    viewType={viewType}
                    onViewTypeChange={onViewTypeChange}
                    tableViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.table',
                    })}
                    singleCardViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.list',
                    })}
                    multiCardsViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.cards',
                    })}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableColumnsDropdown
                    columns={columns}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.label',
                    })}
                    onColumnOrderChange={onColumnOrderChange}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    onReset={onResetColumns}
                    showResetButton
                    resetLabel={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.reset',
                    })}
                />
            </TableToolbarColumn>
        </TableToolbar>
    );
};
